import { useCallback } from 'react';
import { useHistory } from 'react-router';
import { History } from 'history';
import { debounce } from 'lodash';

import { replacingDashOnHyphen } from 'app/lib/replaceDashSymbol';
import { DEBOUNCE_WAIT, SEARCH_MODAL_PARAM_KEY } from 'app/modules/Search/constants';

const setSearchParams = (
  history: History,
  searchParams: URLSearchParams,
  value: string,
) => {
  const correctedValue = replacingDashOnHyphen(value);
  searchParams.set(SEARCH_MODAL_PARAM_KEY, correctedValue);

  const newSearch = searchParams.toString();
  const newPath = `${history.location.pathname}?${newSearch}`;

  history.replace(newPath);
};

export const setSearchParamsWithDebounce = debounce(
  setSearchParams,
  DEBOUNCE_WAIT,
);

export const useSearchParams = () => {
  const history = useHistory();
  const urlParams = new URLSearchParams(history.location.search);

  const hasSearchKey = urlParams.has(SEARCH_MODAL_PARAM_KEY);
  const searchValue = urlParams.get(SEARCH_MODAL_PARAM_KEY) || '';
  const timedSearchString = searchValue.trim();

  const setSearchedValue = useCallback((value: string) => {
    setSearchParamsWithDebounce(history, urlParams, value);
  }, [history, urlParams]);

  const removeSearchKey = useCallback(() => {
    urlParams.delete(SEARCH_MODAL_PARAM_KEY);
    const newSearch = urlParams.toString();
    const newPath = `${history.location.pathname}?${newSearch}`;

    history.replace(newPath);
  }, [urlParams, history.location.pathname, history.replace]);

  return {
    hasSearchKey,
    searchValue,
    timedSearchString,
    setSearchedValue,
    removeSearchKey,
  };
};
